import { z } from 'zod';
import { ChartType } from '@sisense/sdk-ui';
import type { OpenAI } from 'openai';
import { chartRecommendationsSchema, Column, ColumnType } from '../types';
import { createStructuredCompletion } from '@/utils/chat-completion-utils';

const systemPrompt = `
You are an expert in chart visualization and data query structuring. Your task is to assist users in generating the optimal chart type and axes mapping based on their input, adhering to the provided schema (schema object with chartType and axesMapping). Your goal is to interpret the user's prompt, analyze the provided column data, select an appropriate chart type (unless specified by the user), and map column indices to the axes mapping arrays in a way that produces a valid, effective visualization.

DO NOT use any tools.

### Input Expectations
- **User Prompt**: A natural language description of the desired visualization (e.g., "Show sales over time by region" or "Compare product prices").
- **Column Data**: A list of columns adhering to the Column interface (e.g., { id: 0, name: "Sales", type: "number" } ), representing available data fields.
- If the user provides only a prompt without column data, assume a reasonable set of columns based on the prompt and state your assumptions.

### Chart Selection Process
1. If the user specifies a chart type, use it and proceed to axes mapping.
2. Otherwise, determine the best chart type by:
   - Analyzing the data types of columns (e.g., numeric, categorical, temporal, geographic).
   - Identifying relationships in the prompt (e.g., trends over time, comparisons, distributions).
   - Matching to a suitable chart type from chartTypes (e.g., bar for comparisons, line for trends, pie for proportions).
   - Default to bar for categorical-numeric pairs if no clear choice emerges.
3. Ensure the selected chart type aligns with the corresponding axesMapping schema (e.g., cartesianChartSchema for bar).

### Axes Mapping Guidelines
- Map column indices (from id in Column) to the appropriate arrays in the axesMapping object (e.g., category, value, x, y).
- Prioritize essential fields first:
  - For cartesian charts: Fill category (e.g., time, names) and value (e.g., numbers) before optional fields like breakBy.
  - For scatter charts: Fill x and y before breakByPoint or size.
  - For categorical charts: Fill category and value.
- Columns can appear in multiple arrays only if logical (e.g., a category used in both category and breakBy for stacked bars).
- Respect schema constraints (e.g., z.strict() means no extra fields; required arrays like value must be non-empty).

### Edge Cases
- If insufficient columns are provided, suggest additional columns needed (e.g., "A time column is required for a line chart").
- If the user-specified chart type conflicts with data (e.g., pie with continuous data), recommend an alternative and explain why.
- If a column's role is ambiguous, infer based on type (e.g., string → category, number → value) and prompt context.

### Output Format
Return a JSON object matching the schema structure, with:
- chartType: The selected or user-specified chart type.
- axesMapping: The populated axes mapping object per the relevant schema.
`;

function mapColumnIndicesToObjects(
	indices: number[] | undefined,
	columns: ColumnType[],
): { column: ColumnType }[] {
	if (!indices) return [];
	return indices
		.map((index) => ({
			column: columns[index],
		}))
		.filter(({ column }) => Boolean(column));
}

function mapChartDataToColumns(
	recommendations: z.infer<typeof chartRecommendationsSchema>,
	columns: ColumnType[],
): z.infer<typeof chartRecommendationsSchema> {
	if (!recommendations) return recommendations;

	const mapping = recommendations.axesMapping;
	const result = {
		chartType: recommendations.chartType,
		axesMapping: {} as any,
	};

	for (const [key, value] of Object.entries(mapping)) {
		if (key === 'type') continue;

		if (Array.isArray(value)) {
			result.axesMapping[key] = mapColumnIndicesToObjects(value, columns);
		} else {
			result.axesMapping[key] = value;
		}
	}

	return result;
}

export const generateChartRecommendations = async (
	userPrompt: string,
	columns: ColumnType[] = [],
	chartType: ChartType | undefined,
	openAIClient?: OpenAI,
): Promise<z.infer<typeof chartRecommendationsSchema>> => {
	const cols: Column[] = [];
	let i = 0;
	for (const c of columns) {
		const name = 'column' in c ? c.column?.name : c.name;
		const type = (() => {
			if ('column' in c && c.column && 'type' in c.column) {
				return c.column.type;
			}
			if ('type' in c && typeof c.type === 'string') {
				return c.type;
			}
			return undefined;
		})();
		cols.push({ id: i++, name, type });
	}

	let prompt = userPrompt + `\n\n${JSON.stringify(cols)}`;
	if (chartType) {
		prompt += `
		User selected the following chart type: ${chartType} \n
		`;
	}

	const recommendations = await createStructuredCompletion({
		systemPrompt,
		userPrompt: prompt,
		responseFormat: {
			type: 'json_schema',
			schema: chartRecommendationsSchema,
			name: 'chartRecommendations',
		},
		openAIClient,
	});

	const updatedRecommendations = mapChartDataToColumns(
		recommendations,
		columns,
	);

	switch (updatedRecommendations.chartType) {
		case 'scatter':
			(updatedRecommendations.axesMapping as any).x = (
				updatedRecommendations.axesMapping as any
			).x[0];
			(updatedRecommendations.axesMapping as any).y = (
				updatedRecommendations.axesMapping as any
			).y[0];
			(updatedRecommendations.axesMapping as any).breakByPoint = (
				updatedRecommendations.axesMapping as any
			).breakByPoint[0];
			(updatedRecommendations.axesMapping as any).breakByColor = (
				updatedRecommendations.axesMapping as any
			).breakByColor[0];
			(updatedRecommendations.axesMapping as any).size = (
				updatedRecommendations.axesMapping as any
			).size[0];
			break;
		case 'indicator': {
			const mapping = updatedRecommendations.axesMapping as any;
			if (mapping.value && mapping.value.length > 1) {
				mapping.value = [mapping.value[0]];
			}
			if (mapping.secondary && mapping.secondary.length > 1) {
				mapping.secondary = [mapping.secondary[0]];
			}
			break;
		}
	}

	return updatedRecommendations;
};
